import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { assistantApi } from '../api';

const QUICK_ACTIONS = [
  'Welche Kündigungen sind empfohlen?',
  'Welche Risiken gibt es?',
  'Welche Fristen sind kritisch?',
  'Wie hoch sind meine monatlichen Kosten?',
  'Welche Versicherungen habe ich?',
  'Welche Internetverträge habe ich?',
  'Welche Mobilfunkverträge habe ich?',
  'Welche Stromverträge habe ich?',
  'Welche Gasverträge habe ich?',
  'Welche Streaming-Verträge habe ich?',
  'Welche Finanzverträge habe ich?',
];

const CLOUD_MODE_LABELS = {
  lokal_only: 'Nur lokal',
  cloud_allowed: 'Cloud erlaubt',
  cloud_confirm: 'Cloud nach Bestätigung',
};

const DATA_SCOPE_LABELS = {
  summary_only: 'Nur Zusammenfassung senden',
  ocr_allowed: 'OCR-Auszüge senden',
  document_allowed: 'Dokumentinhalte senden',
};

export default function AssistantPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialMessage = searchParams.get('message') || searchParams.get('q') || '';
  const [input, setInput] = useState(initialMessage);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(searchParams.get('conversationId') || null);
  const [conversations, setConversations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingCloud, setPendingCloud] = useState(null);

  useEffect(() => {
    loadSidebarData();
    if (initialMessage) {
      sendMessage(initialMessage);
    }
  }, []);

  async function loadSidebarData() {
    try {
      const [providersRes, settingsRes, conversationsRes] = await Promise.all([
        assistantApi.providers(),
        assistantApi.getSettings(),
        assistantApi.conversations(),
      ]);
      setProviders(providersRes.data.providers || []);
      setSettings(settingsRes.data);
      setConversations(conversationsRes.data.conversations || []);
    } catch {
      // Sidebar-Daten sind optional fuer die Kernfunktion des Chats.
    }
  }

  async function openConversation(id) {
    setLoading(true);
    try {
      const res = await assistantApi.conversation(id);
      setConversationId(res.data.id);
      setMessages(res.data.messages.map(message => ({
        role: message.role,
        content: message.content,
        sources: message.sources || [],
        usedContracts: message.usedContracts || [],
        usedDocuments: message.usedDocuments || [],
        confidence: message.confidence,
        provider: message.provider,
        tokens: message.tokens,
        processingTime: message.processingTime,
        providerFallback: message.providerFallback,
      })));
    } catch {
      // Konversation konnte nicht geladen werden, aktueller Chat bleibt bestehen.
    } finally {
      setLoading(false);
    }
  }

  function startNewConversation() {
    setConversationId(null);
    setMessages([]);
    setPendingCloud(null);
  }

  async function saveSettings(patch) {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      const res = await assistantApi.updateSettings(next);
      setSettings(res.data);
    } catch {
      // Bei Fehler bleibt die lokale Auswahl sichtbar, wird beim naechsten Laden korrigiert.
    }
  }

  const hasMessages = messages.length > 0;
  const latestConfidence = useMemo(() => {
    const assistantMessage = [...messages].reverse().find(item => item.role === 'assistant');
    return assistantMessage?.confidence ?? null;
  }, [messages]);

  async function sendMessage(value = input, options = {}) {
    const message = String(value || '').trim();
    if (!message || loading) return;
    setInput('');
    setLoading(true);
    setPendingCloud(null);
    if (!options.silent) {
      setMessages(current => [...current, { role: 'user', content: message, createdAt: new Date().toISOString() }]);
    }
    try {
      const res = await assistantApi.chat({
        message,
        conversationId,
        provider: settings?.preferred_provider,
        confirmCloud: Boolean(options.confirmCloud),
      });
      setConversationId(res.data.conversationId || conversationId);
      if (res.data.cloudConfirmationRequired) {
        setPendingCloud({ message, provider: res.data.pendingProvider });
      }
      setMessages(current => [...current, {
        role: 'assistant',
        content: res.data.answer,
        sources: res.data.sources || [],
        usedContracts: res.data.usedContracts || [],
        usedDocuments: res.data.usedDocuments || [],
        confidence: res.data.confidence,
        provider: res.data.provider,
        tokens: res.data.tokens,
        processingTime: res.data.processingTime,
        providerFallback: res.data.providerFallback,
        cloudConfirmationRequired: res.data.cloudConfirmationRequired,
        pendingProvider: res.data.pendingProvider,
        createdAt: new Date().toISOString(),
      }]);
      loadSidebarData();
    } catch {
      setMessages(current => [...current, { role: 'assistant', content: 'Der KI-Assistent konnte gerade nicht antworten.', sources: [], confidence: 0 }]);
    } finally {
      setLoading(false);
    }
  }

  function confirmCloudRequest() {
    if (!pendingCloud) return;
    sendMessage(pendingCloud.message, { silent: true, confirmCloud: true });
  }

  function submit(event) {
    event.preventDefault();
    sendMessage();
  }

  return (
    <div style={page}>
      <section style={hero}>
        <div>
          <div style={eyebrow}>KI-Vertragsberater</div>
          <h1 style={title}>Mit deinen Verträgen sprechen</h1>
          <p style={subtitle}>Fristen, Risiken, Kosten, Anbieter, Dokumente, OCR und KI-Analysen werden zusammengeführt.</p>
        </div>
        <div style={providerBox}>
          <span style={muted}>Provider &amp; Datenschutz</span>
          <button type="button" onClick={() => setShowSettings(current => !current)} className="btn-secondary">
            {settings ? `${CLOUD_MODE_LABELS[settings.cloud_mode] || settings.cloud_mode}` : 'Einstellungen'}
          </button>
          {latestConfidence !== null && <strong style={confidence}>{latestConfidence}% Vertrauen</strong>}
        </div>
      </section>

      {showSettings && settings && (
        <SettingsPanel settings={settings} providers={providers} onChange={saveSettings} />
      )}

      <section style={quickGrid}>
        {QUICK_ACTIONS.map(action => (
          <button key={action} type="button" onClick={() => sendMessage(action)} className="btn-secondary">{action}</button>
        ))}
      </section>

      <section style={layout}>
        <aside style={sidebar}>
          <button type="button" onClick={startNewConversation} className="btn-primary">+ Neue Unterhaltung</button>
          <div style={conversationList}>
            {conversations.length === 0 && <div style={muted}>Noch keine gespeicherten Unterhaltungen.</div>}
            {conversations.map(conversation => (
              <button
                key={conversation.id}
                type="button"
                onClick={() => openConversation(conversation.id)}
                style={conversation.id === conversationId ? conversationItemActive : conversationItem}
              >
                <div style={conversationTitle}>{conversation.titel || 'Unterhaltung'}</div>
                {conversation.lastMessage && <div style={conversationPreview}>{conversation.lastMessage}</div>}
              </button>
            ))}
          </div>
        </aside>

        <div style={chatShell}>
          <div style={chatWindow}>
            {!hasMessages ? (
              <div style={emptyState}>
                <h2 style={emptyTitle}>Frag nach Kündigungen, Risiken, Fristen oder Kosten.</h2>
                <p style={muted}>Antworten enthalten Quellen, Verträge, Dokumente und Vertrauensscore.</p>
              </div>
            ) : (
              messages.map((message, index) => (
                <MessageBubble key={`${message.role}-${index}`} message={message} navigate={navigate} />
              ))
            )}
            {loading && <div style={typing}>KI-Assistent wertet Vertragsbestand aus...</div>}
            {pendingCloud && (
              <div style={confirmBox}>
                <span>Cloud-Anfrage an <strong>{pendingCloud.provider}</strong> senden?</span>
                <button type="button" onClick={confirmCloudRequest} style={confirmBtn}>Bestätigen</button>
              </div>
            )}
          </div>
          <form onSubmit={submit} style={composer}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Frage zum Vertragsbestand stellen..."
              style={textarea}
              rows={3}
            />
            <button type="submit" disabled={loading || !input.trim()} className="btn-primary">Senden</button>
          </form>
        </div>
      </section>
    </div>
  );
}

function SettingsPanel({ settings, providers, onChange }) {
  function toggleFallback(name) {
    const current = settings.fallback_providers || [];
    const next = current.includes(name) ? current.filter(item => item !== name) : [...current, name];
    onChange({ fallback_providers: next });
  }

  return (
    <section style={settingsPanel}>
      <div style={settingsCol}>
        <span style={settingsLabel}>Bevorzugter Provider</span>
        <select value={settings.preferred_provider} onChange={e => onChange({ preferred_provider: e.target.value })} style={select}>
          {providers.map(item => (
            <option key={item.name} value={item.name}>
              {item.label}{item.cloud ? (item.configured ? ' (konfiguriert)' : ' (kein Key hinterlegt)') : ''}
            </option>
          ))}
        </select>
      </div>
      <div style={settingsCol}>
        <span style={settingsLabel}>Cloud-Modus</span>
        <select value={settings.cloud_mode} onChange={e => onChange({ cloud_mode: e.target.value })} style={select}>
          {Object.entries(CLOUD_MODE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      <div style={settingsCol}>
        <span style={settingsLabel}>Datenumfang an Cloud-Provider</span>
        <select value={settings.data_scope} onChange={e => onChange({ data_scope: e.target.value })} style={select}>
          {Object.entries(DATA_SCOPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      <div style={settingsCol}>
        <span style={settingsLabel}>Fallback-Reihenfolge</span>
        <div style={fallbackList}>
          {providers.filter(item => item.name !== 'rule-based').map(item => (
            <label key={item.name} style={fallbackItem}>
              <input
                type="checkbox"
                checked={(settings.fallback_providers || []).includes(item.name)}
                onChange={() => toggleFallback(item.name)}
              />
              {item.label}
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}

function MessageBubble({ message, navigate }) {
  const isUser = message.role === 'user';
  return (
    <article style={isUser ? userBubble : assistantBubble}>
      <div style={bubbleMeta}>{isUser ? 'Du' : `KI-Assistent · ${message.provider || 'rule-based'}`}</div>
      <div style={markdown}>{renderMarkdown(message.content)}</div>
      {!isUser && message.providerFallback && (
        <div style={fallbackNotice}>Hinweis: Cloud-Provider war nicht erreichbar, Antwort basiert auf dem regelbasierten Fallback.</div>
      )}
      {!isUser && (
        <div style={answerMeta}>
          <span>Vertrauen: {message.confidence ?? 0}%</span>
          {message.tokens?.estimated && <span>Tokens geschätzt: {message.tokens.estimated}</span>}
          {message.processingTime != null && <span>{message.processingTime} ms</span>}
        </div>
      )}
      {!isUser && message.sources?.length > 0 && (
        <div style={sources}>
          <strong style={{ color: '#f8fafc' }}>Quellen</strong>
          {message.sources.map(source => (
            <button key={`${source.type}-${source.id}-${source.title}`} onClick={() => source.url && navigate(source.url)} className="btn-secondary" style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',textAlign:'left'}}>
              <span style={sourceType}>{source.type}</span>
              <span>{source.title}</span>
              {source.detail && <span style={muted}>{source.detail}</span>}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}

function renderMarkdown(text) {
  return String(text || '').split('\n').map((line, index) => {
    if (line.startsWith('- ')) return <li key={index}>{formatInline(line.slice(2))}</li>;
    if (line.trim() === '') return <br key={index} />;
    return <p key={index} style={{ margin: '0 0 8px' }}>{formatInline(line)}</p>;
  });
}

function formatInline(line) {
  const parts = String(line).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => part.startsWith('**') && part.endsWith('**')
    ? <strong key={index}>{part.slice(2, -2)}</strong>
    : part);
}

const page = { padding: 18, maxWidth: 1200, margin: '0 auto', display: 'grid', gap: 16 };
const hero = { display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap', background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 18 };
const eyebrow = { color: '#38bdf8', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 };
const title = { color: '#f8fafc', fontSize: 32, margin: '4px 0 8px' };
const subtitle = { color: '#cbd5e1', margin: 0, maxWidth: 680 };
const providerBox = { display: 'grid', gap: 6, minWidth: 190 };
const select = { background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: 7, padding: '9px 10px' };
const confidence = { color: '#34d399', fontSize: 13 };
const settingsPanel = { display: 'flex', gap: 16, flexWrap: 'wrap', background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 16 };
const settingsCol = { display: 'grid', gap: 6, minWidth: 200, flex: '1 1 200px' };
const settingsLabel = { color: '#94a3b8', fontSize: 12, fontWeight: 700, textTransform: 'uppercase' };
const fallbackList = { display: 'grid', gap: 6 };
const fallbackItem = { display: 'flex', gap: 8, alignItems: 'center', color: '#cbd5e1', fontSize: 13 };
const quickGrid = { display: 'flex', gap: 8, flexWrap: 'wrap' };
const layout = { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' };
const sidebar = { display: 'grid', gap: 10, minWidth: 220, flex: '1 1 220px', maxWidth: 280, background: '#111827', border: '1px solid #1f2937', borderRadius: 8, padding: 12, alignContent: 'start' };
const conversationList = { display: 'grid', gap: 6, maxHeight: 460, overflowY: 'auto' };
const conversationItem = { display: 'grid', gap: 2, textAlign: 'left', background: '#0f172a', border: '1px solid #263244', color: '#cbd5e1', borderRadius: 7, padding: 9, cursor: 'pointer' };
const conversationItemActive = { ...conversationItem, border: '1px solid #38bdf8' };
const conversationTitle = { fontSize: 13, fontWeight: 700, color: '#f8fafc' };
const conversationPreview = { fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const chatShell = { flex: '3 1 480px', minWidth: 300, background: '#111827', border: '1px solid #1f2937', borderRadius: 8, overflow: 'hidden' };
const chatWindow = { minHeight: 460, maxHeight: '62vh', overflowY: 'auto', padding: 16, display: 'grid', gap: 12, alignContent: 'start' };
const emptyState = { minHeight: 320, display: 'grid', placeItems: 'center', textAlign: 'center' };
const emptyTitle = { color: '#f8fafc', margin: 0 };
const userBubble = { justifySelf: 'end', maxWidth: '82%', background: '#1d4ed8', color: '#f8fafc', borderRadius: 8, padding: 13 };
const assistantBubble = { justifySelf: 'start', maxWidth: '92%', background: '#0f172a', color: '#e5e7eb', border: '1px solid #263244', borderRadius: 8, padding: 13 };
const bubbleMeta = { color: '#94a3b8', fontSize: 12, marginBottom: 8, fontWeight: 800 };
const markdown = { color: '#e5e7eb', lineHeight: 1.5, fontSize: 14 };
const answerMeta = { display: 'flex', gap: 10, flexWrap: 'wrap', color: '#94a3b8', fontSize: 12, marginTop: 10 };
const fallbackNotice = { color: '#fbbf24', fontSize: 12, marginTop: 8 };
const sources = { display: 'grid', gap: 7, marginTop: 12, borderTop: '1px solid #263244', paddingTop: 10 };
const sourceType = { color: '#38bdf8', fontSize: 11, textTransform: 'uppercase', fontWeight: 800 };
const composer = { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, padding: 12, borderTop: '1px solid #1f2937', background: '#0f172a' };
const textarea = { width: '100%', boxSizing: 'border-box', resize: 'vertical', background: '#020617', border: '1px solid #334155', color: '#f8fafc', borderRadius: 8, padding: 11, fontSize: 14 };
const typing = { color: '#94a3b8', fontSize: 13, padding: 10 };
const confirmBox = { display: 'flex', gap: 10, alignItems: 'center', background: '#1c1917', border: '1px solid #fbbf24', color: '#fef3c7', borderRadius: 8, padding: 10, fontSize: 13 };
const confirmBtn = { background: '#fbbf24', color: '#1c1917', border: 0, borderRadius: 6, padding: '6px 12px', fontWeight: 800, cursor: 'pointer' };
const muted = { color: '#94a3b8', fontSize: 13 };
